// org 的 MyCam 軟體設備與 AI 政策（AUTH-04；MyCam 企業場域）：每一列各自儲存（改了就送，要 5 分鐘內重新驗證）。
// 誰執行：require_attestation 由平台在啟用時擋；lock_settings / allow_audio_record / byo_cloud 由 MyCam App 照做；
// allow_personal_account：企業家戶本來就只給工作帳號（org-site §3），開了怎麼放行還沒定——先只顯示、不能改。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isOrgAdmin, orgApi, type MyOrg, type OrgDevicePolicy } from '../../lib/planeApi';
import { errText } from './OrgSection';

const KEYS: { key: keyof OrgDevicePolicy; by: 'platform' | 'app' | 'pending' }[] = [
  { key: 'soft_device.require_attestation', by: 'platform' },
  { key: 'soft_device.lock_settings', by: 'app' },
  { key: 'soft_device.allow_audio_record', by: 'app' },
  { key: 'ai.byo_cloud.allowed', by: 'app' },
  { key: 'soft_device.allow_personal_account', by: 'pending' },
];

export function DevicePolicyCard({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['org-policy', org.org_id], queryFn: () => orgApi.securityPolicy(org) });
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<{ key: string; msg: string } | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  /** 送出中 / 剛存好還沒重抓的值（樂觀顯示；失敗就拿掉、回到伺服器的值）。 */
  const [local, setLocal] = useState<Partial<OrgDevicePolicy>>({});
  const admin = isOrgAdmin(org);
  const cur = q.data;
  if (!cur) return null;
  const base: OrgDevicePolicy = cur.device_policy;
  const dp: OrgDevicePolicy = { ...base, ...local };
  const toggle = async (key: keyof OrgDevicePolicy, v: boolean) => {
    setBusy(key);
    setErr(null);
    setSaved(null);
    setLocal((l) => ({ ...l, [key]: v }));
    try {
      await orgApi.putSecurityPolicy(org, {
        expected_version: cur.version,
        idle_timeout_minutes: cur.idle_timeout_minutes,
        max_sessions: cur.max_sessions,
        ip_allowlist: cur.ip_allowlist ?? [],
        device_policy: { ...base, [key]: v },
      });
      setSaved(key);
      await qc.invalidateQueries({ queryKey: ['org-policy', org.org_id] });
      setLocal((l) => {
        const n = { ...l };
        delete n[key];
        return n;
      });
    } catch (e) {
      setLocal((l) => {
        const n = { ...l };
        delete n[key];
        return n;
      });
      setErr({ key, msg: errText(t, e) });
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="card" aria-label={t('devpolicy.title')}>
      <h2 className="section-title">{t('devpolicy.title')}</h2>
      <p className="muted small">{t('devpolicy.hint')}</p>
      <ul className="plain-list">
        {KEYS.map(({ key, by }) => (
          <li key={key} className="policy-row" data-policy={key}>
            <label className="check">
              <input
                type="checkbox"
                checked={dp[key]}
                disabled={!admin || by === 'pending' || busy !== null}
                onChange={(e) => void toggle(key, e.target.checked)}
              />
              <span>{t(`devpolicy.k_${key.replace(/\./g, '_')}`)}</span>
            </label>
            <span className="small muted">{t(`devpolicy.by_${by}`)}</span>
            {busy === key && <span className="small muted">{t('common.saving')}</span>}
            {saved === key && <span className="small muted">{t('policy.saved')}</span>}
            {err?.key === key && <span className="error small">{err.msg}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
}
